import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  Grid,
} from "@mui/material";

import CustomButton from "../../components/common/button/CustomButton";
import ManageLead from "./lead/ManageLead";
import ManageFilter from "./lead/ManageFilter";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  getAllLeadLogListAction,
  getAllLeadListAction,
  getSingleLeadAction,
  setUpdateLeadAction,
} from "../../store/reducers/leadSlice";
import { FirstPage, LastPage } from "@mui/icons-material";
import WelcomeScreen from "./welcomeScreen/WelcomeScreen";
import FeedbackDialog from "./feedback/FeedbackDialog";
import ManageImportLeads from "./lead/ManageImportLeads";
import {
  getWelcomeClosedAction,
  setNotificationAction,
  setWelcomeClosedAction,
} from "../../store/reducers/mmymSlice";
import { getUserProfile } from "../../store/reducers/userSlice";
import { setSearchParamsAction } from "../../store/reducers/mmymSlice";
import ManageTenant from "./settings/manageClient/ManageTenant";
import { getClientForEditAction } from "../../store/reducers/adminClientsSlice";
import LeadListCard from "./lead/leadList/LeadListCard";
import StatusBadgeNote from "../../components/common/StatusBadgeNote";
import CustomSearchBar from "../../components/header/CustomSearchBar";
import { loadSession, saveSession } from "../../utils/useSession";

interface Lead {
  id: string;
  name: string;
  postcode: string;
  status: string;
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
  filterStatus: string;
  accountName: string;
  b2bAccounts: boolean;
  salesPersonId: string;
}

interface LeadListProps {
  setGenerateId: any;
  tenantId?: any;
}

const ROW_HEIGHT = 50; // px, adjust to your row/card height
const MIN_LIST_LIMIT = 10;
const IMPORT_API_BASE = (
  process.env.REACT_APP_IMPORT_API_BASE_URL ||
  process.env.REACT_APP_BASE_URL ||
  "https://dev-api.managemymoves.co.uk/v1"
).replace(/\/$/, "");
const IMPORT_WS_URL =
  process.env.REACT_APP_IMPORT_WS_URL ||
  "wss://ibmscrm22d.execute-api.ap-south-1.amazonaws.com/v1";
const LIVE_INBOX_POLL_EVERY_MS = 2000;
const LAST_SEEN_INBOX_MESSAGE_ID_STORAGE_KEY = "importLastSeenInboxMessageId";

const decodeJwtPayload = (token: string) => {
  try {
    const [, payload = ""] = token.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      "=",
    );

    return JSON.parse(atob(paddedPayload));
  } catch {
    return null;
  }
};

const resolveTenantId = (userProfile: any, user: any, authToken: string) => {
  const tokenPayload = decodeJwtPayload(authToken);
  const candidates = [
    userProfile?.tenant_id,
    userProfile?.tenantId,
    userProfile?.client?.tenant_id,
    userProfile?.client?.tenantId,
    userProfile?.company?.tenant_id,
    userProfile?.company?.tenantId,
    user?.tenant_id,
    user?.tenantId,
    user?.user?.tenant_id,
    user?.user?.tenantId,
    tokenPayload?.tenantId,
    tokenPayload?.tenant_id,
    tokenPayload?.["custom:tenantId"],
  ];

  const tenantValue = candidates.find((candidate) =>
    String(candidate ?? "").trim(),
  );
  return String(tenantValue ?? "").trim();
};

const normalizeCount = (value: unknown) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeMessageId = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getResponsiveLimit = () => {
  if (typeof window === "undefined") {
    return 10;
  }

  return Math.max(MIN_LIST_LIMIT, Math.floor(window.innerHeight / ROW_HEIGHT));
};

const resolveLeadListRoleType = (roles: string[] = [], activeTab: string) => {
  if (
    roles.includes("Owner") ||
    roles.includes("TENANT") ||
    (roles.length !== 1 && roles[0] === "Surveyor")
  ) {
    return activeTab === "0" ? "Admin" : "Surveyor";
  }

  if (roles.includes("Admin") && roles.includes("Surveyor")) {
    return activeTab === "0" ? "Admin" : "Surveyor";
  }

  if (roles.includes("Admin")) {
    return activeTab === "0" ? "Admin" : "Surveyor";
  }

  if (roles.length === 1 && roles[0] === "Surveyor") {
    return "Surveyor";
  }

  return "Admin";
};

const LeadList: React.FC<LeadListProps> = ({ setGenerateId }) => {
  const lastLimitRef = useRef<number>(-1);
  const lastLeadListRequestRef = useRef<string>("");
  const isNewLeadRefreshRef = useRef(false);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isScreenLarge = useMediaQuery("(max-width:1248px)");
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  // ✅ STEP 1: Load session once at top — used to initialize ALL state below
  const savedSession = loadSession();

  // ✅ STEP 2: All state initialized from session
  const [selectedLead, setSelectedLead] = useState<string | null>(
    savedSession?.selectedLead || null,
  );
  const [selectedOrderBy, setSelectedOrderBy] = useState<any>(
    savedSession?.selectedOrderBy || null,
  );
  const [selectedOrderIn, setSelectedOrderIn] = useState<any>(
    savedSession?.selectedOrderIn || null,
  );

  const [value, setValue] = React.useState<string>(
    savedSession?.activeTab || "0",
  );

  const { searchParams } = useSelector((state: any) => state.mmym);

  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: savedSession?.filterOption?.page || 1,
    limit: savedSession?.filterOption?.limit || getResponsiveLimit(),
    orderBy: savedSession?.filterOption?.orderBy || "created_at",
    orderIn: savedSession?.filterOption?.orderIn || "DESC",
    search:
      savedSession?.filterOption?.search || searchParams?.searchString || "",
    filterStatus:
      savedSession?.filterOption?.filterStatus ||
      searchParams?.filterStatus ||
      "",
    accountName:
      savedSession?.filterOption?.accountName ||
      searchParams?.accountName ||
      "",
      b2bAccounts: savedSession?.filterOption?.b2bAccounts || searchParams?.b2bAccounts || false,
    salesPersonId:
      savedSession?.filterOption?.salesPersonId ||
      searchParams?.salesPersonId ||
      "",
  });

  // Dynamically set limit based on page height, debounce resize, only update if changed
  useEffect(() => {
    let resizeTimeout: any;
    function updateLimit() {
      const newLimit = getResponsiveLimit();
      if (lastLimitRef.current !== newLimit) {
        lastLimitRef.current = newLimit;
        setFilterOption((prev) => {
          if (prev.limit === newLimit) {
            return prev;
          }

          return {
            ...prev,
            limit: newLimit,
          };
        });
      }
    }
    function onResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateLimit, 200);
    }
    updateLimit();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (searchParams) {
      const nextSearch = searchParams?.searchString || "";
      const nextFilterStatus = searchParams?.filterStatus || "";

      setFilterOption((prev) => {
        if (
          // prev.page === 1 &&
          prev.search === nextSearch &&
          prev.filterStatus === nextFilterStatus
        ) {
          return prev;
        }

        const updated = {
          ...prev,
          page: 1,
          search: nextSearch,
          filterStatus: nextFilterStatus,
        };
        // ✅ Save updated filter to session
        saveSession({ filterOption: updated });
        return updated;
      });
    }
  }, [searchParams]);

  const dispatch = useDispatch();
  const {
    leads,
    leadPagination,
    isUpdateLeadSuccess,
    isAddLeadSuccess,
    updateLeadQuoteResponse,
    updateLeadEstimateResponse,
    isAddLeadLogSuccess,
  } = useSelector((state: any) => state.lead);
  const { response } = useSelector((state: any) => state.storage);
  const { confirmationResponse } = useSelector(
    (state: any) => state.confirmation,
  );
  const { userProfile, settingCompanyModulePermission, leadModulePermission } =
    useSelector((state: any) => state.user);
  const { isWelcomeClosed } = useSelector((state: any) => state.mmym);
  const { isCompleteSurveyedSummarySuccess } = useSelector(
    (state: any) => state.leadSurvey,
  );
  const { isSurveyDiarySuccess, isSurveyDiaryUpdateSuccess } = useSelector(
    (state: any) => state.surveyDiary,
  );

  const [title, setTitle] = useState("");
  const { user } = useSelector((state: any) => state?.user);

  const [openLeadImport, setOpenLeadImport] = useState(false);
  const [openTenantModelShow, setOpenTenantModelShow] = useState(false);
  const [hasMailboxMessages, setHasMailboxMessages] = useState(false);
  const [inboxWatchTransport, setInboxWatchTransport] = useState<
    "http" | "websocket"
  >("http");
  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const inboxRequestInFlightRef = useRef(false);
  const latestInboxMessageIdRef = useRef<number | null>(null);
  const lastSeenInboxMessageIdRef = useRef<number | null>(null);

  const COLOR_INFO = [
    {
      label: "Pending",
    },
    {
      label: "In Progress",
    },
    {
      label: "Completed",
    },
  ];

  useEffect(() => {
    const storedId = sessionStorage.getItem("selectedJobLeadId");

    // Existing special case
    if (storedId) {
      setGenerateId(storedId);
      setSelectedLead(storedId);
      saveSession({ selectedLead: storedId });
      sessionStorage.removeItem("selectedJobLeadId");

      return;
    }

    // No leads, nothing to select
    if (!leads || leads.length === 0) {
      return;
    }

    // =========================================================
    // ✅ NEW LEAD CASE
    // =========================================================
    // After adding a new lead, ALWAYS select first lead
    if (isNewLeadRefreshRef.current) {
      const firstId = leads[0]?.generated_id;

      if (firstId) {
        setGenerateId(firstId);
        setSelectedLead(firstId);

        saveSession({
          selectedLead: firstId,
        });
      }

      // Important: reset after selecting first lead
      isNewLeadRefreshRef.current = false;

      return;
    }

    // =========================================================
    // EXISTING BEHAVIOR
    // =========================================================

    const saved = loadSession();

    if (saved?.selectedLead) {
      setSelectedLead(saved.selectedLead);
      setGenerateId(saved.selectedLead);

      return;
    }

    // If nothing is selected, select first lead
    const firstId = leads[0]?.generated_id;

    if (firstId) {
      setGenerateId(firstId);
      setSelectedLead(firstId);

      saveSession({
        selectedLead: firstId,
      });
    }
  }, [leads]);

  // ✅ When a new lead is successfully added:
  // 1. Reset pagination to page 1
  // 2. Force list API refresh
  // 3. Mark that first lead should be selected
  useEffect(() => {
    if (!isAddLeadSuccess) return;

    isNewLeadRefreshRef.current = true;

    // Force API request even if page is already 1
    lastLeadListRequestRef.current = "";

    setFilterOption((prev) => {
      const updated = {
        ...prev,
        page: 1,
      };

      saveSession({
        filterOption: updated,
      });

      return updated;
    });

    // Clear current selection.
    // New first lead will be selected after API response.
    setSelectedLead(null);
    setGenerateId(null);
  }, [isAddLeadSuccess]);

  useEffect(() => {
    if (response?.addStorage) {
      dispatch(getAllLeadListAction({ ...filterOption, roleType: "Admin" }));
    }
  }, [dispatch, response?.addStorage]);

  useEffect(() => {
    if (isWelcomeClosed) {
      dispatch(getUserProfile());
      dispatch(setWelcomeClosedAction(false));
    }
  }, [isWelcomeClosed]);

  const handleClose = () => {
    setOpen(false);
    setOpenTenantModelShow(false);
    setTitle("");
  };

  const handleTenantOpen = () => {
    setOpenTenantModelShow(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };
  const handleOpen = () => {
    setOpen(true);
    setTitle("Add New Lead");
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handleFilter = () => {
    setOpenFilter(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLead(leadId);
    setGenerateId(leadId);
    saveSession({ selectedLead: leadId });
  };

  useEffect(() => {
    const rolesArray = user?.user?.roles || []; // Ensure it's always an array
    const selectedRole = resolveLeadListRoleType(rolesArray, value);

    if (
      rolesArray.length === 1 &&
      rolesArray[0] === "Surveyor" &&
      value !== "1"
    ) {
      setValue("1"); // Force to Surveyor tab
    }

    if (user?.user?.roles?.length > 0) {
      const shouldForceLeadListRefresh =
        isUpdateLeadSuccess ||
        isCompleteSurveyedSummarySuccess ||
        isAddLeadSuccess ||
        updateLeadQuoteResponse ||
        updateLeadEstimateResponse ||
        confirmationResponse ||
        isSurveyDiarySuccess ||
        isAddLeadLogSuccess;

      if (shouldForceLeadListRefresh) {
        // Reset dedupe key so success-driven updates can trigger one fresh list fetch.
        lastLeadListRequestRef.current = "";
      }

      const requestPayload = { ...filterOption, roleType: selectedRole };
      const requestKey = JSON.stringify(requestPayload);

      if (lastLeadListRequestRef.current === requestKey) {
        return;
      }

      lastLeadListRequestRef.current = requestKey;

      dispatch(getAllLeadListAction(requestPayload));
      dispatch(setUpdateLeadAction(false));

      // ❌ Don't call getClientForEditAction if ANY of the success flags is true
      const shouldSkipClientFetch =
        isUpdateLeadSuccess ||
        isCompleteSurveyedSummarySuccess ||
        isAddLeadSuccess;

      if (
        !shouldSkipClientFetch &&
        (user?.user?.roles?.includes("Owner") ||
          user?.user?.roles?.includes("TENANT") ||
          settingCompanyModulePermission?.read)
      ) {
        dispatch(getClientForEditAction());
      }
    }
  }, [
    dispatch,
    filterOption,
    user,
    value,
    isUpdateLeadSuccess,
    isCompleteSurveyedSummarySuccess,
    isAddLeadSuccess,
    updateLeadQuoteResponse,
    updateLeadEstimateResponse,
    confirmationResponse,
    isSurveyDiarySuccess,
    isAddLeadLogSuccess,
  ]);

  const handlePageChange = (newPage: number) => {
    setFilterOption((prev) => {
      const updated = { ...prev, page: newPage };
      saveSession({ filterOption: updated });
      return updated;
    });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    saveSession({ activeTab: newValue, selectedLead: null });

    const userRoles = user?.user?.roles || []; // This is now an array
    let roleType = "";
    // Determine roleType based on selected tab and user's roles
    if (newValue === "0") {
      roleType =
        userRoles.includes("Admin") || userRoles.includes("Owner")
          ? "Admin"
          : userRoles[0] || "";
    } else {
      roleType = userRoles.includes("Surveyor")
        ? "Surveyor"
        : userRoles[0] || "";
    }

    dispatch(
      setSearchParamsAction({
        ...searchParams,
        filterStatus: "",
        searchOption:
          roleType === "Admin" || roleType === "TENANT" ? "lead" : "surveyLead",
      }),
    );

    setSelectedOrderBy(null);
    setSelectedOrderIn(null);
    setFormData({
      orderBy: "",
      orderIn: "",
    });
    setGenerateId(null);
    setSelectedLead(null);
  };

  const closeWelcomeScreen = () => {
    dispatch(getWelcomeClosedAction());
  };

  // for sorting data
  const [formData, setFormData] = useState({
    orderBy: "",
    orderIn: "",
  });

  const authToken = String(user?.accessToken ?? "").trim();
  const tenantId = resolveTenantId(userProfile, user, authToken);

  const applyInboxStatus = (payload: any) => {
    const messageCount = normalizeCount(payload?.messageCount);
    const latestMessageId = normalizeMessageId(payload?.latestMessageId);

    latestInboxMessageIdRef.current = latestMessageId;
    setHasMailboxMessages(messageCount > 0 || latestMessageId !== null);
  };

  const disconnectSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsWsConnected(false);
  };

  const fetchInboxStatus = async () => {
    if (!authToken || !tenantId || inboxRequestInFlightRef.current) {
      return;
    }

    inboxRequestInFlightRef.current = true;

    try {
      const response = await fetch(
        `${IMPORT_API_BASE}/import-leads/inbox-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ tenant_id: tenantId }),
        },
      );

      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};
      if (response.ok) {
        applyInboxStatus(payload);
        setInboxWatchTransport("http");
      }
    } catch {
      // Ignore transient watch failures on list page.
    } finally {
      inboxRequestInFlightRef.current = false;
    }
  };

  const sendInboxStatusOverSocket = () => {
    if (
      !authToken ||
      !tenantId ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return false;
    }

    inboxRequestInFlightRef.current = true;
    wsRef.current.send(
      JSON.stringify({
        action: "inbox-status",
        authorization: authToken,
        tenant_id: tenantId,
      }),
    );
    return true;
  };

  const connectSocket = () => {
    if (!IMPORT_WS_URL.trim() || wsRef.current) {
      return;
    }

    const socket = new WebSocket(IMPORT_WS_URL);
    wsRef.current = socket;

    socket.onopen = () => {
      setIsWsConnected(true);
    };

    socket.onclose = () => {
      wsRef.current = null;
      setIsWsConnected(false);
    };

    socket.onerror = () => {
      setIsWsConnected(false);
      setInboxWatchTransport("http");
    };

    socket.onmessage = (event) => {
      inboxRequestInFlightRef.current = false;

      try {
        const payload = JSON.parse(String(event.data));
        if (payload?.type === "inbox-status" && payload?.status !== "error") {
          applyInboxStatus(payload);
          setInboxWatchTransport("websocket");
          return;
        }

        if (payload?.message === "Forbidden") {
          setInboxWatchTransport("http");
        }
      } catch {
        // Ignore non-inbox socket messages on the list page.
      }
    };
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    lastSeenInboxMessageIdRef.current = normalizeMessageId(
      localStorage.getItem(LAST_SEEN_INBOX_MESSAGE_ID_STORAGE_KEY),
    );
  }, []);

  useEffect(() => {
    if (!authToken || !tenantId) {
      setHasMailboxMessages(false);
      disconnectSocket();
      return;
    }

    connectSocket();

    const runCheck = () => {
      if (document.hidden) {
        return;
      }

      const sentOverSocket = sendInboxStatusOverSocket();
      if (!sentOverSocket) {
        void fetchInboxStatus();
      }
    };

    runCheck();
    const timerId = window.setInterval(runCheck, LIVE_INBOX_POLL_EVERY_MS);

    return () => {
      window.clearInterval(timerId);
      disconnectSocket();
    };
  }, [authToken, tenantId]);

  const importButtonSx = hasMailboxMessages
    ? {
        backgroundColor: "#ce6210",
        color: "#fff",
        "&:hover": {
          backgroundColor: "#1f5a23",
        },
      }
    : undefined;

  const handleDelete = () => {
    setSelectedOrderBy(null);
    setSelectedOrderIn(null);
    setOpenFilter(false);
    const resetFilter = {
      page: 1,
      limit: getResponsiveLimit(),
      orderBy: "created_at",
      orderIn: "DESC",
      search: "",
      filterStatus: "",
      accountName: "",
      b2bAccounts: false,
      salesPersonId: "",
    };
    setFilterOption(resetFilter);
    setFormData({ orderBy: "", orderIn: "" });
    // ✅ Clear sort from session too
    saveSession({
      filterOption: resetFilter,
      selectedOrderBy: null,
      selectedOrderIn: null,
    });
  };

  const handleFilterClose = () => {
    setOpenFilter(false);
    setFormData({
      orderBy: "",
      orderIn: "",
    });
  };

  const handleImportModalHide = (options?: { shouldRefresh?: boolean }) => {
    setOpenLeadImport(false);

    if (!options?.shouldRefresh) {
      return;
    }

    const roleType = resolveLeadListRoleType(user?.user?.roles || [], value);
    const requestPayload = { ...filterOption, roleType };

    lastLeadListRequestRef.current = "";
    dispatch(getAllLeadListAction(requestPayload));

    if (selectedLead) {
      dispatch(getSingleLeadAction(selectedLead));
      dispatch(
        getAllLeadLogListAction({
          page: 1,
          limit: 10,
          orderBy: "created_at",
          orderIn: "DESC",
          leadId: selectedLead,
        }),
      );
    }
  };

  return (
    <>
      {openFilter && (
        <ManageFilter
          open={openFilter}
          filterLeads={filterOption}
          setOpenFilter={setOpenFilter}
          setSelectedOrderBy={setSelectedOrderBy}
          setSelectedOrderIn={setSelectedOrderIn}
          formData={formData}
          setFormData={setFormData}
          onHide={handleFilterClose}
          title="Sorting"
          value={value}
          onApplyFilter={(sortValues: {
            orderBy: string;
            orderIn: string;
            accountName?: string;
            b2bAccounts?: boolean;
            salesPersonId?: string;
          }) => {
            setFilterOption((prev) => {
              const updated = { ...prev, page: 1, ...sortValues };
              saveSession({
                filterOption: updated,
                selectedOrderBy: sortValues.orderBy || null,
                selectedOrderIn: sortValues.orderIn || null,
              });
              return updated;
            });
          }}
        />
      )}

      {openLeadImport && (
        <ManageImportLeads
          open={openLeadImport}
          onHide={handleImportModalHide}
        />
      )}

      {/* welcome Dialog */}
      <WelcomeScreen open={userProfile?.is_new} onHide={closeWelcomeScreen} />

      {/* Feedback Dialog */}
      <FeedbackDialog />

      {/* company details */}
      {openTenantModelShow && (
        <ManageTenant
          selectClient={""}
          open={openTenantModelShow}
          onHide={handleClose}
          title="Edit Client"
          action={"edit"}
        />
      )}
      {/* lead details */}
      {open && (
        <ManageLead
          selectLead={selectedLead}
          open={open}
          onHide={handleClose}
          title={title}
          // listTabValue={value}
        />
      )}
      {/* {open && (
        <ManageCompleteLead
          selectLead={selectedLead}
          open={open}
          onHide={handleClose}
          title={title}
          // listTabValue={value}
        />
      )} */}
      <Box
        sx={{
          // maxWidth: 360,
          // margin: "auto",
          // background: "#fff",
          flexDirection: "column",
          // borderRadius: "5px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "9px",
            background: "#fff",
            // paddingBottom: "16px",
            border: "1px solid #C4CDD5",
          }}
        >
          <div>
            {(user?.user?.roles?.includes("TENANT") ||
              user?.user?.roles?.includes("Admin") ||
              user?.user?.roles?.includes("Owner") ||
              (leadModulePermission?.create &&
                user?.user?.roles?.length !== 1) ||
              user?.user?.roles[0] !== "Surveyor") && (
              <>
                <CustomButton
                  style={{
                    marginRight: "4px",
                    fontSize: "12px",
                    padding: "4px 8px",
                  }}
                  title="Import Leads"
                  onClick={() => setOpenLeadImport(true)}
                  sx={importButtonSx}
                  // disabled
                />
                <CustomButton
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                  title="New Lead"
                  onClick={
                    userProfile?.transportCode ? handleOpen : handleTenantOpen
                  }
                />
              </>
            )}
          </div>
          <CustomButton
            style={{ fontSize: "12px", padding: "4px 8px" }}
            title="Filter"
            variant="outlined"
            onClick={handleFilter}
          />
          {/* <Button
            onClick={handleFilter}
            variant="contained"
            color="secondary"
            sx={{ padding: "4px 8px", minWidth: "0px" }}
          >
            <img src={filterIcon} alt="filter" style={{ width: "20px" }} />
          </Button> */}
        </Box>
        {(user?.user?.roles?.includes("TENANT") ||
          user?.user?.roles?.includes("Admin") ||
          user?.user?.roles?.includes("Owner") ||
          user?.user?.roles?.length !== 1 ||
          user?.user?.roles[0] !== "Surveyor") && (
          <Tabs
            value={value}
            onChange={handleChange}
            textColor="secondary"
            indicatorColor="secondary"
            aria-label="secondary tabs example"
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: "none" } }} // 🔹 hides the underline
            sx={{
              background: "#fff",
              fontSize: "12px",
              fontWeight: 500,
              marginBottom: "10px",
              minHeight: "12px",
              padding: "14px 7px",
              border: "1px solid #C4CDD5",
              height: "32px",
              "& .MuiTab-root": {
                textTransform: "none", // keep normal case
                borderRadius: "4px",
                margin: "0 4px",
                minHeight: "12px",
                height: "32px",
                backgroundColor: "#f0f0f0", // default background
              },
              "& .Mui-selected": {
                backgroundColor: "#02906f",
                color: "#fff !important",
                fontWeight: 600,
                minHeight: "12px",
                height: "32px",
              },
            }}
          >
            <Tab value="0" label="All Leads" />
            <Tab value="1" label="Survey Leads" />
          </Tabs>
        )}
        <>
          <CustomSearchBar value={value} />
          {leads.length > 0 ? (
            <>
              {value === "1" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px", // spacing between badges
                    flexWrap: "wrap", // optional if you want wrapping on smaller screens
                    px: 2,
                    py: "0px",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ width: "100%", textAlign: "center", padding: "0px" }}
                  >
                    Survey Status
                  </Typography>
                  {COLOR_INFO?.map((info, index) => (
                    <StatusBadgeNote key={index} status={info.label} />
                  ))}
                </Box>
              )}
              <Box sx={{ width: "100%", paddingBottom: 4, paddingTop: 1 }}>
                <Grid
                  container
                  spacing={0}
                  alignItems="center"
                  sx={{
                    background: "#E5F4F1",
                    color: "#637381",
                    padding: "10px 10px",
                    // borderTopLeftRadius: "10px",
                    // borderTopRightRadius: "10px",
                    marginBottom: "4px",
                    border: "1px solid #C4CDD5",
                  }}
                >
                  <Grid item xs={6} sx={{ borderRight: "1px solid #C4CDD5" }}>
                    <Typography variant="body2" fontWeight={600}>
                      Customer List
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs
                    sx={{
                      textAlign: "center",
                      borderRight: "1px solid #C4CDD5",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      F
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs
                    sx={{
                      textAlign: "center",
                      borderRight: "1px solid #C4CDD5",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      A
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs
                    sx={{
                      textAlign: "center",
                      borderRight: "1px solid #C4CDD5",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      M
                    </Typography>
                  </Grid>
                  <Grid item xs sx={{ textAlign: "center" }}>
                    <Typography variant="body2" fontWeight={600}>
                      L
                    </Typography>
                  </Grid>
                </Grid>
                {leads.map((lead: any) => (
                  <LeadListCard
                    value={value}
                    lead={lead}
                    selectedLead={selectedLead}
                    setGenerateId={handleSelectLead}
                    setSelectedLead={setSelectedLead}
                  />
                ))}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                padding: "2px 10px",
                height: "60vh",
                display: "flex",
                textAlign: "center",
                alignItems: "center",
                color: "#4A4A4A",
                justifyContent: "center",
              }}
            >
              {user?.user?.roles?.includes("TENANT") ||
              user?.user?.roles?.includes("Admin") ||
              user?.user?.roles?.includes("Owner") ? (
                value === "0" ? (
                  <>Add a New Lead or Import!</>
                ) : (
                  <> Assigned surveys will appear here, nothing assigned yet</>
                )
              ) : value === "1" ? (
                <> Assigned surveys will appear here, nothing assigned yet</>
              ) : (
                "Assigned surveys will appear here, nothing assigned yet"
              )}
            </Box>
          )}
          {leads.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: 2,
                  alignItems: "center",
                  borderTop: "2px solid #d5d5d5",
                }}
              >
                <Button
                  size="small"
                  onClick={() =>
                    handlePageChange(leadPagination?.currentPage - 1)
                  }
                  variant="contained"
                  color="secondary"
                  sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
                  disabled={leadPagination?.currentPage === 1}
                >
                  <FirstPage sx={{ fontSize: "16px" }} />
                </Button>
                <Typography
                  sx={{
                    fontSize:
                      isMdUp && !isScreenLarge
                        ? "13px"
                        : isScreenLarge
                          ? "12px"
                          : "12px",
                    textAlign: "center",
                  }}
                >
                  {leadPagination?.totalItems} listings found. Page{" "}
                  {leadPagination?.currentPage} of {leadPagination?.totalPages}.
                </Typography>
                <Button
                  onClick={() =>
                    handlePageChange(leadPagination?.currentPage + 1)
                  }
                  variant="contained"
                  color="secondary"
                  sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
                  disabled={!leadPagination?.hasNextPage}
                >
                  <LastPage sx={{ fontSize: "16px" }} />
                </Button>
              </Box>

              {selectedOrderBy && !openFilter && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: 0,
                    paddingBottom: "4px",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    label={`Sorting By: ${selectedOrderBy}${
                      selectedOrderIn ? ` | ${selectedOrderIn}` : ""
                    }`}
                    onDelete={handleDelete}
                  />
                </Box>
              )}
            </>
          )}
        </>
      </Box>
    </>
  );
};

export default LeadList;
